/**
 * Asset manifest — the indirection layer that makes "skins" a later swap with
 * zero engine changes. The engine only ever asks for LOGICAL keys
 * (player_small_idle, tile_solid_top, goal_flag_a, sfx jump…). A manifest maps
 * those keys to vendored theme files; LevelFormat.theme picks which manifest.
 *
 * Each theme is a self-contained folder under src/assets/themes/<name>/ with its
 * own import.meta.glob. A skin (Brief 003) = a new themes/<name>/ dir + manifest.
 *
 * RESILIENCE (decision X3): resolution never hard-crashes the running game. A key
 * resolves through a fallback chain (requested theme → overworld default → the
 * small-form equivalent → magenta placeholder), so when a future form adds new
 * required keys, EXISTING skins degrade gracefully instead of breaking. In DEV a
 * registered manifest missing any REQUIRED key throws loudly at module load; in
 * PROD it logs and leans on the fallback chain.
 */

import MAGENTA_URL from './placeholder_magenta.png?url';
import {
  GENERATED_THEME_FILES,
  GENERATED_MANIFESTS,
  GENERATED_THEME_DISPLAY,
} from './themes/generated-manifests';

/** Logical → source-filename maps for one theme. */
export interface AssetManifest {
  /** Theme key, matched against LevelFormat.theme. */
  theme: string;
  /** Logical sprite key → filename inside src/assets/themes/<theme>/. */
  sprites: Record<string, string>;
  /** Logical SFX key → filename. Leave a key out (or unbound) if the pack lacks it. */
  sounds: Record<string, string>;
  /**
   * Optional bespoke theme track — a filename inside src/assets/themes/<theme>/ (e.g.
   * 'theme_music.ogg'), resolved to a URL by composeManifest exactly like a sound key.
   * `null` (most themes) means "no theme track" — World exposes musicKey through to
   * main.ts, which falls back to the global PLAY_TRACKS rotation when it's null
   * (POLISH_02 #9). Render-side only; the sim never reads this.
   */
  musicKey: string | null;
}

/** A manifest with every filename resolved to a loadable URL. */
export interface ResolvedManifest {
  theme: string;
  sprites: Record<string, string>; // logical key → URL
  sounds: Record<string, string>; // logical key → URL
  musicKey: string | null; // resolved bespoke-track URL, or null → caller falls back to PLAY_TRACKS
}

// ── Required-key SSOT ─────────────────────────────────────────────────────────
// THE single source of truth for what a complete sprite/sound set must provide.
// Recounted against the live overworld manifest: 25 sprites + 5 sounds.
// (player 12 · goomba 4 · tiles 4 · goal 3 · bg 2 = 25.)

export const REQUIRED_SPRITE_KEYS = [
  // Player — SMALL set (6)
  'player_small_idle', 'player_small_walk_1', 'player_small_walk_2',
  'player_small_jump', 'player_small_duck', 'player_small_hit',
  // Player — BIG set (6)
  'player_big_idle', 'player_big_walk_1', 'player_big_walk_2',
  'player_big_jump', 'player_big_duck', 'player_big_hit',
  // Enemy goomba (4)
  'goomba_walk_1', 'goomba_walk_2', 'goomba_rest', 'goomba_flat',
  // Tiles (4)
  'tile_solid_top', 'tile_solid_fill', 'tile_solid_block', 'tile_semisolid',
  // Goal flag (3)
  'goal_flag_a', 'goal_flag_b', 'goal_off',
  // Background (2)
  'bg_far', 'bg_sky',
] as const;

export const REQUIRED_SOUND_KEYS = [
  'jump', 'bump', 'coin', 'hurt', 'select',
] as const;

/**
 * Additive SEMANTIC SFX keys (POLISH_01 #1) — the action-specific palette (shoot/stomp/slash/…).
 * NOT in REQUIRED_SOUND_KEYS on purpose: the module-load gate + the length-5 SSOT test stay
 * unaffected and no theme is forced to declare them. Unioned into composeManifest so EVERY theme
 * resolves them through the fallback chain (→ overworld clips), exactly like ENTITY_SPRITE_KEYS.
 * Overworld maps each to a vendored .ogg (Ludo-generated 2026-07-01) below.
 */
export const ENTITY_SOUND_KEYS: string[] = [
  'shoot', 'stomp', 'slash', 'spring', 'break', 'checkpoint', 'fanfare',
];

/**
 * Additive entity sprite keys (spike / coin / flyer_* etc.), NOT in
 * REQUIRED_SPRITE_KEYS so themes lacking them do NOT fail the module-load
 * validateManifest gate. Unioned into resolveManifest so every theme RESOLVES them
 * through the fallback chain (→ overworld art) instead of missing entirely. Brief
 * 007 P2 appends keys here + maps them in OVERWORLD_MANIFEST. Empty in P1 — this is
 * only the mechanism, so P2 is pure-append.
 */
export const ENTITY_SPRITE_KEYS: string[] = [
  'spike',
  'coin',
  'coin_spin',
  'flyer_bat_fly',
  'flyer_bat_hang',
  'flyer_bee_fly',
  'flyer_fly_fly',
  // Brief 008 archetype species. WALKER variants render <variant>_walk_1/2/rest/flat;
  // HOPPER renders <variant>_rest/jump/flat; AMBUSH renders <variant>_hang/drop(/flat).
  // overworld maps them below; other themes fall back to overworld art via resolveManifest.
  'snail_walk_1', 'snail_walk_2', 'snail_rest', 'snail_flat',
  'mouse_walk_1', 'mouse_walk_2', 'mouse_rest', 'mouse_flat',
  'spiny_walk_1', 'spiny_walk_2', 'spiny_rest', 'spiny_flat',
  'frog_rest', 'frog_jump', 'frog_flat',
  'spider_hang', 'spider_drop', 'spider_flat',
  // Brief 009 interactive blocks — `?`-block (active 2-frame shimmer + used), brick,
  // spring (rest + compressed). overworld maps them below; other themes fall back.
  'block_q', 'block_q_alt', 'block_used',
  'brick',
  'spring', 'spring_down',
  // Brief 010 power-up pickups — heart refill, extra-heart container, star (invuln).
  // overworld maps them below; other themes fall back to overworld art.
  'item_heart', 'item_heart_container', 'item_star',
  // Brief 011 terrain mechanisms — moving platform, conveyor belt, portal, door.
  // overworld maps them below; other themes fall back to overworld art.
  'platform', 'conveyor', 'warp', 'door',
  // Brief 012 hazards — lava field (2-frame molten shimmer), saw blade (2-frame spin),
  // fire-bar orb, thwomp crusher. overworld maps them below; other themes fall back.
  'lava', 'lava_alt', 'saw', 'saw_spin', 'fireball', 'thwomp',
  // Brief 013 checkpoint — inactive banner + activated (lit) banner. Distinct colour
  // from GOAL_FLAG (green). overworld maps them below; other themes fall back.
  'checkpoint', 'checkpoint_active',
  // Brief 014 variant-object kit — crumble block, dream block, launch pad (+ fire frame),
  // bumper (+ hit frame), exit switch/gate (+ on/open frames), fan (2-frame), laser on/off.
  // overworld maps them below; other themes fall back to overworld art.
  'crumble', 'dreamblock',
  'launchpad', 'launchpad_fire',
  'bumper', 'bumper_hit',
  'exitswitch', 'exitswitch_on',
  'exitgate', 'exitgate_open',
  'fan', 'fan_alt',
  'laser_on', 'laser_off',
  // Brief 014 P1 — mine, cracked wall, move block, note block, falling platform, gold, ring.
  'mine', 'crackedwall', 'moveblock', 'spring_note', 'platform_falling', 'gold', 'ring',
  // RUN-AND-GUN — player bullet (CC0 draw) + enemy bullet (CC0 draw) + the step-3 enemy roster
  // (bespoke Ludo art vendored 64² into overworld; other themes fall back to overworld art).
  // NOT in REQUIRED_SPRITE_KEYS → no module-load throw.
  'bullet', 'bullet_enemy',
  'gunner_rest', 'gunner_fire', 'gunner_walk_1', 'gunner_walk_2',
  'armored_rest', 'armored_walk_1', 'armored_walk_2', 'armored_fire',
  // step-4 emplacements — aim-tracking turret (rest + fire) + grunt spawner (closed + open).
  'turret_rest', 'turret_fire',
  'spawner_rest', 'spawner_open',
  // step-4 last-2 — aerial gunship (1 rigid frame) + arc-lobber (rest + throw) + its grenade.
  'strafer_fly',
  'lobber_rest', 'lobber_throw',
  'grenade',
  // POLISH_02 #5 — 4-bit autotile edge caps + corners (World.ts buildTiles → tileAutotile.ts
  // pickTileKey). Additive: NOT in REQUIRED_SPRITE_KEYS, so a theme lacking them still passes
  // the module-load gate and resolves through the fallback chain (→ overworld edge art) exactly
  // like every other ENTITY_SPRITE_KEYS entry — a theme that never vendors these renders EXACTLY
  // as before (tools/vendor-015-edge-tiles.ts vendors them for overworld first).
  'tile_solid_top_left', 'tile_solid_top_right',
  'tile_solid_left', 'tile_solid_right',
  // POLISH_02 #6 — render-only DECOR props (bush/rock/fence/sign/cloud/mushroom/grass tuft).
  // Additive: NOT in REQUIRED_SPRITE_KEYS. Vendored 64² into overworld (tools/vendor-decor.ts);
  // other themes fall back to overworld art via the usual chain.
  'decor_bush', 'decor_rock', 'decor_fence', 'decor_sign',
  'decor_cloud', 'decor_mushroom', 'decor_grasstuft',
];

// ── Per-theme file tables ─────────────────────────────────────────────────────
// Vite statically rewrites each glob to hashed asset URLs. One glob per theme
// keeps themes self-contained (a skin in Brief 003 just adds its own block).

const OVERWORLD_FILE_URLS = import.meta.glob('./themes/overworld/*.{png,ogg}', {
  eager: true,
  query: '?url',
  import: 'default',
}) as Record<string, string>;

const ALIEN_FILE_URLS = import.meta.glob('./themes/alien/*.{png,ogg}', {
  eager: true,
  query: '?url',
  import: 'default',
}) as Record<string, string>;

const THEME_FILES: Record<string, Record<string, string>> = {
  overworld: OVERWORLD_FILE_URLS,
  alien: ALIEN_FILE_URLS,
  // Brief 006: batch-ingested themes. Their per-theme `?url` globs live in the
  // generated file, so their texture binaries stay separate hashed assets (lazy,
  // loaded on swap) exactly like the hand-authored themes above.
  ...GENERATED_THEME_FILES,
};

/** Resolve a (theme, filename) to its built URL, or undefined if absent. */
function fileUrl(theme: string, filename: string | undefined): string | undefined {
  if (!filename) return undefined;
  return THEME_FILES[theme]?.[`./themes/${theme}/${filename}`];
}

/**
 * The "overworld" theme — Kenney New Platformer Pack (CC0).
 * Player SMALL = beige character set, BIG = green set (so a form swap is visibly
 * a different avatar, not just different numbers). Enemy = normal slime.
 */
export const OVERWORLD_MANIFEST: AssetManifest = {
  theme: 'overworld',
  sprites: {
    // Player — SMALL (beige)
    player_small_idle: 'character_beige_idle.png',
    player_small_walk_1: 'character_beige_walk_a.png',
    player_small_walk_2: 'character_beige_walk_b.png',
    player_small_jump: 'character_beige_jump.png',
    player_small_duck: 'character_beige_duck.png',
    player_small_hit: 'character_beige_hit.png',
    // Player — BIG (green)
    player_big_idle: 'character_green_idle.png',
    player_big_walk_1: 'character_green_walk_a.png',
    player_big_walk_2: 'character_green_walk_b.png',
    player_big_jump: 'character_green_jump.png',
    player_big_duck: 'character_green_duck.png',
    player_big_hit: 'character_green_hit.png',
    // Enemy (placeholder goomba = slime)
    goomba_walk_1: 'slime_normal_walk_a.png',
    goomba_walk_2: 'slime_normal_walk_b.png',
    goomba_rest: 'slime_normal_rest.png',
    goomba_flat: 'slime_normal_flat.png',
    // Tiles — SOLID uses a 4-bit neighbor-mask autotile (POLISH_02 #5): a top/fill 2-way pick
    // PLUS edge caps + corners on a run (World.ts buildTiles → tileAutotile.pickTileKey).
    tile_solid_top: 'terrain_grass_block_top.png',
    tile_solid_fill: 'terrain_dirt_block_center.png',
    tile_solid_block: 'terrain_grass_block.png', // standalone full block (isolated 1x1)
    tile_solid_top_left: 'terrain_grass_block_top_left.png', // left end of a surface run (vendor-015)
    tile_solid_top_right: 'terrain_grass_block_top_right.png', // right end of a surface run (vendor-015)
    tile_solid_left: 'terrain_grass_block_left.png', // left edge of a buried run (vendor-015)
    tile_solid_right: 'terrain_grass_block_right.png', // right edge of a buried run (vendor-015)
    tile_semisolid: 'block_planks.png',
    // Goal flag (2-frame wave + the cleared/off frame)
    goal_flag_a: 'flag_green_a.png',
    goal_flag_b: 'flag_green_b.png',
    goal_off: 'flag_off.png',
    // Background layers
    bg_far: 'background_color_hills.png',
    bg_sky: 'background_solid_sky.png',
    // Entities (Brief 006 P2)
    spike: 'spike.png',
    coin: 'coin.png',
    coin_spin: 'coin_spin.png',
    flyer_bat_fly: 'flyer_bat_fly.png',
    flyer_bat_hang: 'flyer_bat_hang.png',
    flyer_bee_fly: 'flyer_bee_fly.png',
    flyer_fly_fly: 'flyer_fly_fly.png',
    // Entities (Brief 008 archetype species — vendored 64² from Kenney extended-enemies)
    snail_walk_1: 'snail_walk_1.png',
    snail_walk_2: 'snail_walk_2.png',
    snail_rest: 'snail_rest.png',
    snail_flat: 'snail_flat.png',
    mouse_walk_1: 'mouse_walk_1.png',
    mouse_walk_2: 'mouse_walk_2.png',
    mouse_rest: 'mouse_rest.png',
    mouse_flat: 'mouse_flat.png',
    spiny_walk_1: 'spiny_walk_1.png',
    spiny_walk_2: 'spiny_walk_2.png',
    spiny_rest: 'spiny_rest.png',
    spiny_flat: 'spiny_flat.png',
    frog_rest: 'frog_rest.png',
    frog_jump: 'frog_jump.png',
    frog_flat: 'frog_flat.png',
    spider_hang: 'spider_hang.png',
    spider_drop: 'spider_drop.png',
    spider_flat: 'spider_flat.png',
    // Interactive blocks (Brief 009 — vendored 64² from kenney_platformer-bricks +
    // deluxe Base pack Items; see tools/vendor-009-blocks.ts)
    block_q: 'block_q.png',
    block_q_alt: 'block_q_alt.png',
    block_used: 'block_used.png',
    brick: 'brick.png',
    spring: 'spring.png',
    spring_down: 'spring_down.png',
    // Power-up pickups (Brief 010 — vendored 64² from kenney_platformer-art-deluxe;
    // see tools/vendor-010-items.ts). heart = HUD full heart, container = Candy heart
    // (distinct), star = Items star.
    item_heart: 'item_heart.png',
    item_heart_container: 'item_heart_container.png',
    item_star: 'item_star.png',
    // Terrain mechanisms (Brief 011 — original CC0 art drawn by tools/vendor-011-terrain.ts)
    platform: 'platform.png',
    conveyor: 'conveyor.png',
    warp: 'warp.png',
    door: 'door.png',
    // Hazards (Brief 012 — original CC0 art drawn by tools/vendor-012-hazards.ts). lava
    // is stretched across a region (renderSize); saw/fireball/thwomp are 64² square.
    lava: 'lava.png',
    lava_alt: 'lava_alt.png',
    saw: 'saw.png',
    saw_spin: 'saw_spin.png',
    fireball: 'fireball.png',
    thwomp: 'thwomp.png',
    // Checkpoint banner (Brief 013 — original CC0 art drawn by tools/vendor-013-checkpoint.ts).
    // Inactive = grey furled pennant; active = bright cyan raised pennant (distinct from
    // the green GOAL_FLAG).
    checkpoint: 'checkpoint.png',
    checkpoint_active: 'checkpoint_active.png',
    // Variant-object kit (Brief 014 — original CC0 art drawn by tools/vendor-014-objects.ts).
    crumble: 'crumble.png',
    dreamblock: 'dreamblock.png',
    launchpad: 'launchpad.png',
    launchpad_fire: 'launchpad_fire.png',
    bumper: 'bumper.png',
    bumper_hit: 'bumper_hit.png',
    exitswitch: 'exitswitch.png',
    exitswitch_on: 'exitswitch_on.png',
    exitgate: 'exitgate.png',
    exitgate_open: 'exitgate_open.png',
    fan: 'fan.png',
    fan_alt: 'fan_alt.png',
    laser_on: 'laser_on.png',
    laser_off: 'laser_off.png',
    // Variant-object kit P1 (Brief 014 — tools/vendor-014b-objects-p1.ts).
    mine: 'mine.png',
    crackedwall: 'crackedwall.png',
    moveblock: 'moveblock.png',
    spring_note: 'spring_note.png',
    platform_falling: 'platform_falling.png',
    gold: 'gold.png',
    ring: 'ring.png',
    // RUN-AND-GUN player bullet + enemy bullet (original CC0 art — tools/vendor-runandgun.ts).
    bullet: 'bullet.png',
    bullet_enemy: 'bullet_enemy.png',
    // RUN-AND-GUN step-3 enemies (bespoke Ludo art, vendored 64² — tools/vendor-rungun-enemies.ts).
    gunner_rest: 'gunner_rest.png',
    gunner_fire: 'gunner_fire.png',
    gunner_walk_1: 'gunner_walk_1.png',
    gunner_walk_2: 'gunner_walk_2.png',
    armored_rest: 'armored_rest.png',
    armored_walk_1: 'armored_walk_1.png',
    armored_walk_2: 'armored_walk_2.png',
    armored_fire: 'armored_fire.png',
    turret_rest: 'turret_rest.png',
    turret_fire: 'turret_fire.png',
    spawner_rest: 'spawner_rest.png',
    spawner_open: 'spawner_open.png',
    strafer_fly: 'strafer_fly.png',
    lobber_rest: 'lobber_rest.png',
    lobber_throw: 'lobber_throw.png',
    grenade: 'grenade.png',
    // POLISH_02 #6 — render-only decoration (Kenney platformer-art-deluxe Base pack, vendored
    // 64² — see tools/vendor-decor.ts). Drawn behind gameplay entities; no sim body.
    decor_bush: 'decor_bush.png',
    decor_rock: 'decor_rock.png',
    decor_fence: 'decor_fence.png',
    decor_sign: 'decor_sign.png',
    decor_cloud: 'decor_cloud.png',
    decor_mushroom: 'decor_mushroom.png',
    decor_grasstuft: 'decor_grasstuft.png',
  },
  sounds: {
    jump: 'sfx_jump.ogg',
    bump: 'sfx_bump.ogg',
    coin: 'sfx_coin.ogg',
    hurt: 'sfx_hurt.ogg',
    select: 'sfx_select.ogg',
    // POLISH_01 #1 — action-specific SFX (Ludo-generated 2026-07-01, mp3→ogg). Additive
    // (ENTITY_SOUND_KEYS); every other theme falls back to these overworld clips.
    shoot: 'sfx_shoot.ogg',
    stomp: 'sfx_stomp.ogg',
    slash: 'sfx_slash.ogg',
    spring: 'sfx_spring.ogg',
    break: 'sfx_break.ogg',
    checkpoint: 'sfx_checkpoint.ogg',
    fanfare: 'sfx_fanfare.ogg',
  },
  musicKey: null,
};

/**
 * The "alien" theme — Kenney Platformer Pack Redux (CC0). A sci-fi reskin proving
 * the indirection layer: same 25 logical keys, a wholly different look, ZERO engine
 * render-code change. Source art was normalized to overworld footprints (player
 * 128×128 / enemy·tile·flag 64×64 / bg 256×256) so spriteScale + anchors carry
 * over unchanged. Player SMALL = green astronaut, BIG = blue astronaut (a form swap
 * is a visibly different avatar). Enemy = purple slime. Tiles = the Planet set.
 * SFX are the overworld jingles (Redux ships no audio) copied in for self-containment.
 */
export const ALIEN_MANIFEST: AssetManifest = {
  theme: 'alien',
  sprites: {
    // Player — SMALL (green astronaut)
    player_small_idle: 'alienGreen_stand.png',
    player_small_walk_1: 'alienGreen_walk1.png',
    player_small_walk_2: 'alienGreen_walk2.png',
    player_small_jump: 'alienGreen_jump.png',
    player_small_duck: 'alienGreen_duck.png',
    player_small_hit: 'alienGreen_hit.png',
    // Player — BIG (blue astronaut)
    player_big_idle: 'alienBlue_stand.png',
    player_big_walk_1: 'alienBlue_walk1.png',
    player_big_walk_2: 'alienBlue_walk2.png',
    player_big_jump: 'alienBlue_jump.png',
    player_big_duck: 'alienBlue_duck.png',
    player_big_hit: 'alienBlue_hit.png',
    // Enemy (purple slime: base + squished move-frame + flattened dead)
    goomba_walk_1: 'slimePurple.png',
    goomba_walk_2: 'slimePurple_move.png',
    goomba_rest: 'slimePurple.png',
    goomba_flat: 'slimePurple_dead.png',
    // Tiles — Planet set (surface top, buried center, standalone block, thin one-way)
    tile_solid_top: 'planetMid.png',
    tile_solid_fill: 'planetCenter.png',
    tile_solid_block: 'planet.png',
    tile_semisolid: 'planetHalf_mid.png',
    // Goal flag (2-frame wave + the down/off frame)
    goal_flag_a: 'flagYellow1.png',
    goal_flag_b: 'flagYellow2.png',
    goal_off: 'flagYellow_down.png',
    // Background layers
    bg_far: 'blue_land.png',
    bg_sky: 'blue_grass.png',
  },
  sounds: {
    jump: 'sfx_jump.ogg',
    bump: 'sfx_bump.ogg',
    coin: 'sfx_coin.ogg',
    hurt: 'sfx_hurt.ogg',
    select: 'sfx_select.ogg',
  },
  musicKey: null,
};

const MANIFESTS: Record<string, AssetManifest> = {
  overworld: OVERWORLD_MANIFEST,
  alien: ALIEN_MANIFEST,
  // Brief 006: batch-ingested themes (gen-theme.ts). Uniform key→"<key>.png" maps;
  // the module-load validateManifest gate below covers them too.
  ...GENERATED_MANIFESTS,
};

/**
 * Return the REQUIRED sprite/sound keys this manifest fails to define. Empty =
 * complete. (Only checks declaration, not that the file exists on disk — the
 * fallback chain handles a declared-but-missing file at resolve time.)
 */
export function validateManifest(m: AssetManifest): string[] {
  const missing: string[] = [];
  for (const k of REQUIRED_SPRITE_KEYS) if (!(k in m.sprites)) missing.push(`sprite:${k}`);
  for (const k of REQUIRED_SOUND_KEYS) if (!(k in m.sounds)) missing.push(`sound:${k}`);
  return missing;
}

// Module-load gate (X3): loud in dev, tolerant in prod.
for (const [theme, m] of Object.entries(MANIFESTS)) {
  const missing = validateManifest(m);
  if (missing.length) {
    const msg = `AssetManifest: theme "${theme}" is missing required keys: ${missing.join(', ')}`;
    if (import.meta.env.DEV) throw new Error(msg);
    else console.error(msg);
  }
}

/** player_<set>_<state> → player_small_<state> (else undefined). */
function smallFormKey(key: string): string | undefined {
  const parts = key.split('_');
  if (parts[0] !== 'player' || parts.length < 3 || parts[1] === 'small') return undefined;
  return `player_small_${parts.slice(2).join('_')}`;
}

/** Resolve one sprite key through the fallback chain. Always returns a URL. */
function resolveSpriteUrl(theme: string, key: string): string {
  // 1. requested theme's own mapping
  const own = fileUrl(theme, MANIFESTS[theme]?.sprites[key]);
  if (own) return own;
  // 2. same key on the overworld default
  const def = fileUrl('overworld', OVERWORLD_MANIFEST.sprites[key]);
  if (def) return def;
  // 3. small-form equivalent on the overworld default
  const sm = smallFormKey(key);
  if (sm) {
    const small = fileUrl('overworld', OVERWORLD_MANIFEST.sprites[sm]);
    if (small) return small;
  }
  // 4. magenta placeholder — visible "missing asset" marker, never a crash
  if (import.meta.env.DEV) console.warn(`AssetManifest: sprite "${key}" fell through to magenta on theme "${theme}"`);
  return MAGENTA_URL;
}

/** Resolve one sound key (requested → overworld). Missing is non-fatal → omit. */
function resolveSoundUrl(theme: string, key: string): string | undefined {
  return fileUrl(theme, MANIFESTS[theme]?.sounds[key]) ?? fileUrl('overworld', OVERWORLD_MANIFEST.sounds[key]);
}

/**
 * Resolve a theme's `musicKey` (a filename inside `themes/<theme>/`, e.g. 'theme_music.ogg')
 * to its built URL. `null` (no bespoke track — most themes) stays `null`: the caller (Music
 * player wiring in main.ts) falls back to the global PLAY_TRACKS rotation in that case. A
 * key that fails to resolve to a real file (typo, file not vendored) also degrades to
 * `null` rather than crashing — same non-fatal discipline as resolveSoundUrl.
 */
function resolveMusicUrl(theme: string, musicKey: string | null): string | null {
  if (!musicKey) return null;
  return fileUrl(theme, musicKey) ?? null;
}

// ── Per-category palette (personal skin mix) ──────────────────────────────────
// A whole-theme swap (Brief 003) binds EVERY logical key to one theme. A palette
// lets the player mix: tiles from theme A, enemies from B, player from C. Every
// logical key is classified into one of 6 CATEGORIES by its prefix; a Palette maps
// category → source theme (an unset category falls back to the compose base). This
// is pure asset-URL routing — it never touches the deterministic sim, so it is
// replay-safe exactly like the single-theme case.

/** The six mix-and-match categories the palette customizer exposes. */
export type SkinCategory = 'player' | 'enemies' | 'tiles' | 'goal' | 'background' | 'objects';

/** Fixed display + signature order for the categories. */
export const SKIN_CATEGORIES: readonly SkinCategory[] = [
  'player', 'enemies', 'tiles', 'goal', 'background', 'objects',
];

/** category → source theme. An absent category resolves through the compose base. */
export type Palette = Partial<Record<SkinCategory, string>>;

/** Human label for a category (customizer rows). */
export function categoryLabel(c: SkinCategory): string {
  switch (c) {
    case 'player': return 'Player';
    case 'enemies': return 'Enemies';
    case 'tiles': return 'Tiles';
    case 'goal': return 'Goal';
    case 'background': return 'Background';
    case 'objects': return 'Objects';
  }
}

/** One representative sprite key per category — the swatch/preview art. */
export const CATEGORY_PREVIEW_KEY: Record<SkinCategory, string> = {
  player: 'player_small_idle',
  enemies: 'goomba_walk_1',
  tiles: 'tile_solid_top',
  goal: 'goal_flag_a',
  background: 'bg_far',
  objects: 'coin',
};

/** Living-creature key heads → the "enemies" category. Non-creature hazards
 *  (spike/lava/saw/thwomp/mine/laser…) fall through to "objects". */
const ENEMY_HEADS = new Set([
  'goomba', 'snail', 'mouse', 'spiny', 'frog', 'spider', 'flyer',
  'gunner', 'armored', // run-and-gun step-3 enemies → the 'enemies' skin category
  'turret', 'spawner', // run-and-gun step-4 emplacements
  'strafer', 'lobber', // step-4 last-2 (grenade → objects, not an enemy head)
]);

/** Classify a logical sprite key into its palette category (prefix-driven). */
export function keyCategory(key: string): SkinCategory {
  if (key.startsWith('player_')) return 'player';
  if (key.startsWith('tile_')) return 'tiles';
  if (key.startsWith('bg_')) return 'background';
  if (key.startsWith('goal_') || key === 'checkpoint' || key === 'checkpoint_active') return 'goal';
  if (ENEMY_HEADS.has(key.split('_')[0])) return 'enemies';
  return 'objects';
}

/** Deterministic identity string for a (base, palette) pair — used as the Pixi
 *  Assets bundle key + the swap dedupe key. Empty palette collapses to the base
 *  theme name so a "no override" composite is byte-identical to a plain theme. */
export function paletteSignature(base: string, palette: Palette): string {
  const parts = SKIN_CATEGORIES.filter((c) => palette[c]).map((c) => `${c}=${palette[c]!}`);
  return parts.length ? `${base}|${parts.join('|')}` : base;
}

/**
 * Compose a manifest from a base theme + a per-category palette: every logical key
 * resolves through the theme named by `palette[keyCategory(key)] ?? base`, then the
 * usual per-key fallback chain (→ overworld → small-form → magenta). Sounds always
 * come from the base (the palette is visual-only). `resolveManifest(theme)` is the
 * empty-palette case. The returned `theme` field is the composite SIGNATURE.
 */
export function composeManifest(base: string, palette: Palette = {}): ResolvedManifest {
  const m = MANIFESTS[base];
  if (!m) throw new Error(`AssetManifest: unknown theme "${base}"`);

  const sprites: Record<string, string> = {};
  for (const key of new Set<string>([...REQUIRED_SPRITE_KEYS, ...ENTITY_SPRITE_KEYS, ...Object.keys(m.sprites)])) {
    const src = palette[keyCategory(key)] ?? base;
    sprites[key] = resolveSpriteUrl(src, key);
  }
  const sounds: Record<string, string> = {};
  for (const key of new Set<string>([...REQUIRED_SOUND_KEYS, ...ENTITY_SOUND_KEYS, ...Object.keys(m.sounds)])) {
    const url = resolveSoundUrl(base, key);
    if (url) sounds[key] = url;
  }
  // Music is base-theme-only (like sounds — the palette is visual-only) and resolves through
  // its OWN theme's glob (not a cross-theme fallback chain like sprites/sounds): a theme with
  // no bespoke track intentionally stays null so the caller uses the global PLAY_TRACKS rotation.
  const musicKey = resolveMusicUrl(base, m.musicKey);
  return { theme: paletteSignature(base, palette), sprites, sounds, musicKey };
}

/** Resolve a single theme key → manifest with every filename rewritten to a URL.
 *  The empty-palette special case of composeManifest (kept for call-site clarity). */
export function resolveManifest(theme: string): ResolvedManifest {
  return composeManifest(theme, {});
}

/**
 * Resolve a palette SIGNATURE (`base` or `base|player=alien|tiles=candy`, as produced by
 * {@link paletteSignature}) back to a composite manifest. This lets the whole render /
 * Pixi-bundle pipeline keep passing a single `theme: string` — the per-category mix lives
 * entirely in the string and is parsed here, at the one resolver boundary. A plain theme
 * name (no `|`) is just the empty-palette case.
 */
export function resolveComposite(sig: string): ResolvedManifest {
  const parts = sig.split('|');
  const base = parts[0];
  const palette: Palette = {};
  for (let i = 1; i < parts.length; i++) {
    const eq = parts[i].indexOf('=');
    if (eq <= 0) continue;
    const cat = parts[i].slice(0, eq) as SkinCategory;
    const theme = parts[i].slice(eq + 1);
    if (SKIN_CATEGORIES.includes(cat) && theme) palette[cat] = theme;
  }
  return composeManifest(base, palette);
}

/** All registered theme keys (for the skin picker / tests). */
export function registeredThemes(): string[] { return Object.keys(MANIFESTS); }

/** Human label for the skin picker. Generated themes carry a displayName; the two
 *  hand-authored themes fall back to a title-cased key. */
export function themeDisplayName(theme: string): string {
  return GENERATED_THEME_DISPLAY[theme] ?? theme.charAt(0).toUpperCase() + theme.slice(1);
}

/** Direct access for tests / validation tooling. */
export function getManifest(theme: string): AssetManifest | undefined { return MANIFESTS[theme]; }
